import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import {
  MAT_MENU_DEFAULT_OPTIONS,
  MatMenuDefaultOptions,
  MatMenuModule,
  MatMenuTrigger,
} from '@angular/material/menu';
import { IS_EMBEDDED } from '@lassi/config';
import { QuickSearchStore, SearchResultService } from '@lassi/webapp-shared';
import { nanoid } from 'nanoid';
import { QuickSearchCategoriesComponent } from './quick-search-categories.component';
import { QuickSearchLayerCheckComponent } from './quick-search-layer-check.component';
import { QuickSearchResultsComponent } from './quick-search-results.component';

@Component({
  selector: 'lassi-quick-search',
  imports: [
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatIcon,
    MatChipsModule,
    MatMenuModule,
    MatButtonModule,
    MatListModule,
    QuickSearchCategoriesComponent,
    QuickSearchLayerCheckComponent,
    QuickSearchResultsComponent,
  ],
  template: `
    <div class="quick-search-wrapper">
      <div
        #searchContainer
        class="search-container"
        [class.landing]="landing()"
        [class.embedded]="isEmbedded"
        [class.elevation]="!landing()"
        [class.menuOpen]="resultsMenuTrigger.menuOpen"
      >
        <lassi-quick-search-categories #categories />
        <div class="divider"></div>
        <button
          class="trigger"
          #resultsMenuTrigger="matMenuTrigger"
          [matMenuTriggerFor]="resultsMenu"
        >
          results
        </button>
        <label [for]="id()" aria-label="Quick Search">
          <mat-icon> search</mat-icon>
        </label>
        <input
          [id]="id()"
          matInput
          [value]="store.query()"
          placeholder="Type to search..."
          autocomplete="off"
          #input
          (focus)="handleFocus()"
          (input)="handleSearchQueryChange($event)"
        />
        @if (store.query()) {
          <button mat-icon-button (click)="clear()">
            <mat-icon> close</mat-icon>
          </button>
        }
      </div>
      <mat-menu #resultsMenu="matMenu" class="results-menu no-scrollbar">
        <ng-template matMenuContent>
          <lassi-quick-search-results
            [width]="searchContainer.clientWidth"
            [landing]="landing()"
            [resultsTrigger]="resultsTrigger()"
            (goToMap)="goToMap.emit()"
          />
          @if (!landing()) {
            <lassi-quick-search-layer-check
              [width]="searchContainer.clientWidth"
            />
          }
        </ng-template>
      </mat-menu>
    </div>
  `,
  styles: `
    @use 'quick-search' as qs;

    @include qs.quickSearch();

    .quick-search-wrapper {
      position: relative;
    }
  `,
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: {
        overlayPanelClass: ['results-menu-overlay'],
        hasBackdrop: false,
      } as MatMenuDefaultOptions,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickSearchComponent {
  readonly store = inject(QuickSearchStore);
  readonly searchResultService = inject(SearchResultService);
  readonly isEmbedded = inject(IS_EMBEDDED);

  readonly id = input(nanoid(5));
  readonly landing = input(false, {
    transform: booleanAttribute,
  });

  readonly goToMap = output<void>();

  handleSearchQueryChange(event: Event) {
    if (!(event instanceof InputEvent)) return;
    const { target } = event;
    if (!(target instanceof HTMLInputElement)) return;

    this.store.setQuery(target.value);
    this.searchResultService.clearSearchResults();
  }

  clear() {
    this.store.clearQuery();
    this.searchResultService.clearSearchResults();
  }

  readonly resultsTrigger =
    viewChild.required<MatMenuTrigger>('resultsMenuTrigger');
  readonly categoriesComponent = viewChild.required(
    QuickSearchCategoriesComponent,
  );

  readonly elementRef = inject(ElementRef<HTMLElement>);

  @HostListener('document:pointerdown', ['$event.target'])
  handleOtherClicks(target: EventTarget | null) {
    if (
      this.resultsTrigger().menuOpen &&
      target instanceof HTMLElement &&
      !this.elementRef.nativeElement.contains(target) &&
      !this.categoriesComponent()
        .searchTypeMenuContainer()
        .nativeElement.contains(target) &&
      !target.closest('.results-menu')
    ) {
      this.resultsTrigger().closeMenu();
    }

    if (
      this.categoriesComponent().searchTypeMenuTrigger().menuOpen &&
      target instanceof HTMLElement &&
      !this.categoriesComponent()
        .searchTypeMenuContainer()
        .nativeElement.contains(target)
    ) {
      this.categoriesComponent().searchTypeMenuTrigger().closeMenu();
    }
  }

  handleFocus() {
    this.resultsTrigger().openMenu();
  }
}
